import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { practitionerKeys, type Practitioner } from '@/features/practitioners/api'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Usuários da clínica ativa (docs/api/admin.md, "Usuários"; RN-ADM-10 a 16). O que a tela lista e
 * edita é o vínculo da pessoa com a clínica: o `id` é o do vínculo (`:membershipId`), e `userId` é
 * a pessoa. Os nomes de campo são os de `apps/api/supabase/functions/admin/users/schemas.ts`.
 */

export type MemberRole = 'manager' | 'receptionist' | 'practitioner'
export type UserStatus = 'invited' | 'active' | 'blocked' | 'removed'
export type AccessMode = 'invite' | 'temporary_password'

/** Quem agiu; `id` nulo é a própria Westhetics (carga inicial, implantação). */
export interface ActorRef {
  id: string | null
  name: string
}

export interface ClinicUser {
  id: string
  userId: string
  name: string
  email: string
  role: MemberRole
  /** Só com `role = practitioner`: a prestadora do vínculo, com o nome como foi cadastrado. */
  practitioner: { id: string; name: string } | null
  status: UserStatus
  blockedAt: string | null
  blockedBy: ActorRef | null
  mustChangePassword: boolean
  /** Validade do convite ainda não aceito; `null` para quem já tem senha (definitiva ou temporária). */
  inviteExpiresAt: string | null
  lastSignInAt: string | null
  /** Preenchido quando o vínculo foi removido ou substituído por uma troca de papel. */
  archivedAt: string | null
  createdAt: string
  createdBy: ActorRef
}

export type UserFilter = 'active' | 'blocked'

export interface UserListMeta extends ListMeta {
  counts: Record<UserFilter, number>
}

/** Corpo de `POST /admin/users`. `practitionerId` só vai com o papel prestador (RN-ADM-12). */
export interface CreateUserInput {
  name: string
  email: string
  role: MemberRole
  practitionerId?: string
  accessMode: AccessMode
}

/**
 * Como a pessoa entra. A senha temporária vem uma única vez (RN-ADM-10): na repetição do mesmo
 * pedido (mesma chave de idempotência) a API devolve a resposta guardada, com `temporaryPassword`
 * nulo. Quando o e-mail já é de alguém do sistema, o modo pedido é ignorado.
 */
export type CreatedAccess =
  | { mode: 'invite'; inviteExpiresAt: string; emailSent: boolean }
  | { mode: 'temporary_password'; temporaryPassword: string | null; emailSent: boolean }
  | { mode: 'existing_membership'; emailSent: boolean }

export interface CreatedUser {
  user: ClinicUser
  access: CreatedAccess
}

export interface PasswordReset {
  temporaryPassword: string
  mustChangePassword: true
}

export interface ResentInvite {
  inviteExpiresAt: string
  emailSent: boolean
}

/** Corpo de `PUT /admin/users/:membershipId/role` (RN-ADM-11): a justificativa vai para a trilha. */
export interface ChangeRoleInput {
  role: MemberRole
  practitionerId?: string
  justification: string
}

/**
 * A prestadora como a escolha do papel prestador precisa dela (`GET /admin/practitioners`): quem é
 * e se já tem usuário ligado. O cadastro completo é da área de Prestadores.
 */
export type PractitionerOption = Pick<
  Practitioner,
  'id' | 'name' | 'professionName' | 'fullRegistration' | 'user'
>

export const userKeys = {
  all: ['users'] as const,
  list: (params: object) => ['users', 'list', params] as const,
  detail: (id: string) => ['users', 'detail', id] as const,
}

// Debaixo de `practitionerKeys.all`: salvar ou arquivar uma prestadora refaz também esta escolha.
const practitionerOptionsKey = [...practitionerKeys.all, 'options'] as const

export function useUsers(params: {
  filter: UserFilter
  search?: string
  page?: number
  perPage?: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () =>
      api.list<ClinicUser, UserListMeta>('/admin/users', {
        filter: params.filter,
        search: params.search,
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    placeholderData: (previous) => previous,
  })
}

export function useUser(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: userKeys.detail(id ?? ''),
    queryFn: () => api.get<ClinicUser>(`/admin/users/${id}`),
    enabled: Boolean(id),
  })
}

/**
 * As prestadoras ativas da clínica, para ligar o usuário de papel prestador (RN-ADM-12). Só é
 * pedida quando a tela precisa: ao escolher o papel, ou para dizer o nome de quem já está ligada.
 */
export function usePractitionerOptions(enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: practitionerOptionsKey,
    queryFn: async () => {
      // A API pagina de 50 em 50: o select precisa de todas, então lê página a página.
      const options: PractitionerOption[] = []
      for (let page = 1; ; page++) {
        const result = await api.list<PractitionerOption>('/admin/practitioners', {
          filter: 'active',
          sort: 'name',
          page,
          perPage: 50,
        })
        options.push(...result.data)
        if (page >= result.meta.totalPages) return options
      }
    },
    enabled,
  })
}

/** Toda escrita que devolve o vínculo atualiza o detalhe na hora e refaz as listas. */
function useStoreUser() {
  const queryClient = useQueryClient()
  return (user: ClinicUser) => {
    queryClient.setQueryData(userKeys.detail(user.id), user)
    return queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
  }
}

/** Criar, trocar papel e remover mudam qual prestadora tem usuário: a escolha é refeita. */
function useRefreshPractitioners() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: practitionerKeys.all })
}

export function useCreateUser() {
  const api = useApi()
  const store = useStoreUser()
  const refreshPractitioners = useRefreshPractitioners()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api.post<CreatedUser>('/admin/users', input, { idempotencyKey: idempotency.keyFor(input) }),
    onSuccess: (created) => {
      idempotency.reset()
      void refreshPractitioners()
      return store(created.user)
    },
  })
}

/** `PUT /admin/users/:membershipId` só altera o nome (RN-ADM-11). */
export function useUpdateUser(id: string) {
  const api = useApi()
  const store = useStoreUser()
  return useMutation({
    mutationFn: (name: string) => api.put<ClinicUser>(`/admin/users/${id}`, { name }),
    onSuccess: store,
  })
}

/** Bloquear e liberar valem na hora, sem corpo (RN-ADM-13). */
export function useSetUserAccess(id: string) {
  const api = useApi()
  const store = useStoreUser()
  return useMutation({
    mutationFn: (action: 'block' | 'unblock') =>
      api.post<ClinicUser>(`/admin/users/${id}/${action}`),
    onSuccess: store,
  })
}

/** Cada chamada gera uma senha nova e encerra as sessões da pessoa (RN-ADM-14). */
export function useResetPassword(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<PasswordReset>(`/admin/users/${id}/reset-password`),
    // Sem esperar a releitura: a senha aparece assim que a API responde.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: userKeys.all }),
  })
}

/** Reemite o link de definir senha por mais 7 dias; o anterior deixa de valer (RN-ADM-10). */
export function useResendInvite(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<ResentInvite>(`/admin/users/${id}/resend-invite`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: userKeys.all }),
  })
}

/**
 * Trocar o papel arquiva este vínculo e cria outro (RN-ADM-11): a resposta é o vínculo novo, com
 * outro `id`. O antigo continua consultável, já arquivado.
 */
export function useChangeRole(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const store = useStoreUser()
  const refreshPractitioners = useRefreshPractitioners()
  return useMutation({
    mutationFn: (input: ChangeRoleInput) => api.put<ClinicUser>(`/admin/users/${id}/role`, input),
    onSuccess: (replacement) => {
      void refreshPractitioners()
      // O vínculo antigo fica vencido no cache, sem recarregar: a tela segue para o novo.
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id), refetchType: 'none' })
      return store(replacement)
    },
  })
}

/** Remover arquiva o vínculo, com a justificativa na trilha (RN-ADM-15). Não restaura. */
export function useRemoveUser(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const refreshPractitioners = useRefreshPractitioners()
  return useMutation({
    mutationFn: (justification: string) => api.delete(`/admin/users/${id}`, { justification }),
    onSuccess: () => {
      void refreshPractitioners()
      // O detalhe fica vencido no cache, sem recarregar: a tela volta para a lista.
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id), refetchType: 'none' })
      return queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
    },
  })
}
